import { fileUrl } from "@/lib/api";
import { inspectKicadHrefOffThread } from "@/lib/kicad-inspect-client";
import {
  KiCanvasHost,
  bindKiCanvasHost,
} from "@/lib/kicanvas-host";
import {
  KICANVAS_EMPTY_CONTENT,
  KICANVAS_ICON_FONT,
  KICANVAS_SCRIPT_SRC,
  collectKiCanvasSourceIssues,
  createKiCanvasResolver,
  documentKindForPath,
  hasWebGL2,
  isStaleRevisionLoad,
  isKiCanvasDisposedError,
  isKiCanvasRuntimeError,
  kicanvasEmptyStateMessage,
  kicanvasVfsName,
  kicadSourcePaths,
  normalizeTreePath,
  pathFromKiCanvasPage,
  subscribeKiCanvasSheetChange,
  type KiCanvasSourceIssue,
} from "@/lib/kicanvas-sources";
import { announceFilesViewerMounted, waitForKiCanvasEmbed } from "@/lib/files-viewer-open";
import {
  KICANVAS_VIEWER_AUTOLOAD_MAX_BYTES,
  KICANVAS_VIEWER_FETCH_TIMEOUT_MS,
  viewerLoadFailureMessage,
} from "@/lib/kicanvas-viewer-load";
import { getWorkbenchSelectionStore } from "@/lib/selection-store";
import { Button } from "@/components/ui/button";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

export type KicadFileViewProps = {
  org: string;
  proj: string;
  revId: string;
  gitOid?: string | null;
  selectedPath: string;
  tree: string[];
  /** Revision catalog `byte_size` by workspace path (from GET …/catalog). */
  catalogByteSizes?: ReadonlyMap<string, number | null>;
  /** False until GET …/revisions/:rev/catalog settles for the active revision. */
  catalogReady?: boolean;
  /** Router replace navigation for KiCanvas sheet changes (keeps `?rev=` + `?file=` in sync). */
  onFilesUrlChange?: (update: { rev?: string; file: string }) => void;
};

function catalogByteSize(
  catalog: ReadonlyMap<string, number | null> | undefined,
  path: string,
): number | null | undefined {
  if (!catalog || catalog.size === 0) return undefined;
  const norm = normalizeTreePath(path);
  if (catalog.has(norm)) return catalog.get(norm);
  return null;
}

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type KiCanvasEmbedEl = HTMLElement & {
  custom_resolver: ((name: string) => URL) | null;
};

let scriptPromise: Promise<void> | null = null;

export function ensureKiCanvasScript(doc: Document = document): Promise<void> {
  if (doc.querySelector("kicanvas-embed") && customElements.get("kicanvas-embed")) {
    return Promise.resolve();
  }
  if (customElements.get("kicanvas-embed")) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const existing = doc.querySelector<HTMLScriptElement>(`script[src="${KICANVAS_SCRIPT_SRC}"]`);
  scriptPromise = new Promise((resolve, reject) => {
    const onReady = () => resolve();
    const onError = () => reject(new Error("KiCanvas script failed to load"));
    if (existing) {
      if (customElements.get("kicanvas-embed")) {
        resolve();
        return;
      }
      existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener("error", onError, { once: true });
      return;
    }
    const script = doc.createElement("script");
    script.src = KICANVAS_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", onReady, { once: true });
    script.addEventListener("error", onError, { once: true });
    doc.head.appendChild(script);
  });
  return scriptPromise;
}

export async function inspectKiCanvasSources(
  candidates: string[],
  org: string,
  proj: string,
  revId: string,
  fetchImpl: typeof fetch = fetch,
  opts: { signal?: AbortSignal; tree?: string[] } = {},
): Promise<{
  sources: string[];
  existing: Set<string>;
  invalid: Map<string, string>;
  texts: Record<string, string>;
  issues: KiCanvasSourceIssue[];
}> {
  const existing = new Set<string>();
  const invalid = new Map<string, string>();
  const texts: Record<string, string> = {};
  const present = opts.tree?.length ? new Set(opts.tree.map(normalizeTreePath)) : null;
  const wanted = candidates.filter((path) => {
    const norm = normalizeTreePath(path);
    return present ? present.has(norm) : Boolean(norm);
  });

  const inspectOne = async (path: string): Promise<void> => {
    if (opts.signal?.aborted) return;
    const href = fileUrl(org, proj, revId, path);
    try {
      const inspected = await inspectKicadHrefOffThread(path, href, fetchImpl, opts.signal);
      if (!inspected.ok) {
        invalid.set(path, inspected.reason);
        return;
      }
      existing.add(path);
      texts[path] = inspected.text;
    } catch (err) {
      if (opts.signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      invalid.set(path, err instanceof Error ? err.message : String(err));
    }
  };

  const selected = wanted[0];
  if (selected) {
    await inspectOne(selected);
  }
  const rest = wanted.slice(1);
  if (rest.length && !opts.signal?.aborted) {
    await Promise.all(rest.map((path) => inspectOne(path)));
  }
  const sources = wanted.filter((path) => existing.has(path));
  return {
    sources,
    existing,
    invalid,
    texts,
    issues: collectKiCanvasSourceIssues(wanted, existing, invalid),
  };
}

export function KicadFileView({
  org,
  proj,
  revId,
  gitOid,
  selectedPath,
  tree,
  catalogByteSizes,
  catalogReady = true,
  onFilesUrlChange,
}: KicadFileViewProps) {
  const webgl = hasWebGL2();
  const candidates = useMemo(() => kicadSourcePaths(tree, selectedPath), [tree, selectedPath]);
  const selected = normalizeTreePath(selectedPath);
  const [sources, setSources] = useState<string[]>([]);
  const [sourceTexts, setSourceTexts] = useState<Record<string, string>>({});
  const [inspectDone, setInspectDone] = useState(false);
  const [issues, setIssues] = useState<KiCanvasSourceIssue[]>([]);
  const [selectedError, setSelectedError] = useState<string | null>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [missingNotice, setMissingNotice] = useState<string[]>([]);
  const [embedLoaded, setEmbedLoaded] = useState(false);
  const resolver = useMemo(
    () =>
      createKiCanvasResolver(org, proj, revId, tree, (name) => {
        setMissingNotice((prev) => (prev.includes(name) ? prev : [...prev, name]));
      }),
    [org, proj, revId, tree],
  );
  const docs = useMemo(
    () =>
      sources.map((path) => ({
        path,
        kind: documentKindForPath(path),
        src: fileUrl(org, proj, revId, path),
        name: kicanvasVfsName(path),
        text: sourceTexts[path] ?? "",
      })),
    [sources, sourceTexts, org, proj, revId],
  );
  const embedRef = useRef<KiCanvasEmbedEl | null>(null);
  const revRef = useRef(revId);
  const candidateKey = `${revId}:${selected}:${candidates.join("|")}`;
  const candidateKeyRef = useRef("");
  const mountedRef = useRef(false);
  revRef.current = revId;
  const selectedKind = documentKindForPath(selected);
  const [iconFontReady, setIconFontReady] = useState(() => {
    if (typeof document === "undefined" || !document.fonts) return true;
    return document.fonts.check(KICANVAS_ICON_FONT);
  });
  const [manualLoadConfirmed, setManualLoadConfirmed] = useState(false);
  const [loadPending, setLoadPending] = useState(false);

  const deferredInspectPaths = useMemo(() => {
    return candidates.filter((path) => {
      const size = catalogByteSize(catalogByteSizes, path);
      return size != null && size > KICANVAS_VIEWER_AUTOLOAD_MAX_BYTES;
    });
  }, [candidates, catalogByteSizes]);
  const deferInspect =
    catalogReady &&
    deferredInspectPaths.length > 0 &&
    !manualLoadConfirmed &&
    catalogByteSizes != null;

  useEffect(() => {
    setManualLoadConfirmed(false);
    setLoadPending(false);
  }, [candidateKey]);

  useEffect(() => {
    if (iconFontReady || typeof document === "undefined" || !document.fonts) return;
    let cancelled = false;
    void document.fonts.load(KICANVAS_ICON_FONT).then(() => {
      if (!cancelled) setIconFontReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [iconFontReady]);

  useEffect(() => {
    const ac = new AbortController();
    const loadRev = revId;
    const sameMount = candidateKeyRef.current === candidateKey;
    const revChanged = !candidateKeyRef.current.startsWith(`${revId}:`);
    if (!sameMount) {
      candidateKeyRef.current = candidateKey;
      mountedRef.current = false;
      setEmbedLoaded(false);
      if (revChanged) {
        setSources([]);
        setSourceTexts({});
        setInspectDone(false);
        setRuntimeError(null);
        setSelectedError(null);
        setMissingNotice([]);
        setIssues([]);
      }
    }
    void (async () => {
      if (!catalogReady) {
        setInspectDone(false);
        return;
      }
      if (candidates.length === 0) {
        setInspectDone(true);
        return;
      }
      if (deferInspect) {
        setInspectDone(true);
        setSources([]);
        setSourceTexts({});
        setIssues([]);
        setSelectedError(null);
        return;
      }
      setLoadPending(true);
      let timeoutSignal: AbortSignal | undefined;
      try {
        timeoutSignal = AbortSignal.timeout(KICANVAS_VIEWER_FETCH_TIMEOUT_MS);
      } catch {
        timeoutSignal = undefined;
      }
      const signal =
        timeoutSignal && typeof AbortSignal.any === "function"
          ? AbortSignal.any([ac.signal, timeoutSignal])
          : ac.signal;
      let inspected;
      try {
        inspected = await inspectKiCanvasSources(candidates, org, proj, revId, fetch, {
          signal,
          tree,
        });
      } catch (err) {
        if (ac.signal.aborted || isStaleRevisionLoad(loadRev, revRef.current)) return;
        setInspectDone(true);
        setSelectedError(viewerLoadFailureMessage(err, signal));
        setLoadPending(false);
        return;
      } finally {
        if (!ac.signal.aborted) setLoadPending(false);
      }
      if (ac.signal.aborted || isStaleRevisionLoad(loadRev, revRef.current)) return;
      setSources(inspected.sources);
      setSourceTexts(inspected.texts);
      setInspectDone(true);
      const selectedIssue = inspected.issues.find((issue) => issue.path === selected);
      setSelectedError(
        selectedIssue
          ? kicanvasEmptyStateMessage(selectedIssue.message, documentKindForPath(selectedIssue.path))
          : null,
      );
      setIssues(inspected.issues.filter((issue) => issue.path !== selected));
    })();
    return () => {
      ac.abort();
    };
  }, [candidateKey, candidates, catalogReady, deferInspect, org, proj, revId, selected, selectedKind, tree]);

  const onConfirmLargeLoad = useCallback(() => {
    setManualLoadConfirmed(true);
  }, []);

  const sourceKey = sources.join("|");
  useEffect(() => {
    if (!selectedPath) return;
    const host = new KiCanvasHost({
      gitOid: gitOid ?? null,
      documentPath: selectedPath,
    });
    const loadDocs =
      docs.length > 0
        ? docs
        : [
            {
              path: selectedPath,
              kind: selectedKind,
              src: fileUrl(org, proj, revId, selectedPath),
              name: kicanvasVfsName(selectedPath),
            },
          ];
    void host.loadDocument(loadDocs);
    const stop = bindKiCanvasHost(host, getWorkbenchSelectionStore());
    if (!webgl || !inspectDone || selectedError || sources.length === 0) {
      return () => {
        stop();
        host.dispose();
      };
    }
    void ensureKiCanvasScript().catch((err) => {
      setRuntimeError(err instanceof Error ? err.message : String(err));
    });
    const embed = embedRef.current;
    const onEmbedError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      const raw = detail?.message || (event as ErrorEvent).message || "KiCanvas failed to load this document.";
      if (isKiCanvasDisposedError(raw) || mountedRef.current) return;
      const message = kicanvasEmptyStateMessage(raw, selectedKind);
      if (sources[0] === selected && selectedKind === "schematic") {
        setRuntimeError(message);
      } else {
        setIssues((prev) =>
          prev.some((issue) => issue.message === message)
            ? prev
            : [...prev, { path: selected, kind: "invalid", message }],
        );
      }
    };
    const containRuntime = (raw: string) => {
      if (isKiCanvasDisposedError(raw)) return true;
      if (!isKiCanvasRuntimeError(raw)) return false;
      if (!embed || (typeof document !== "undefined" && !document.contains(embed))) return true;
      if (mountedRef.current || selectedKind !== "schematic") return true;
      setRuntimeError(KICANVAS_EMPTY_CONTENT);
      return true;
    };
    const onWindowError = (event: ErrorEvent) => {
      const raw = event.message || (event.error instanceof Error ? event.error.message : "");
      if (containRuntime(raw)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const raw = reason instanceof Error ? reason.message : String(reason ?? "");
      if (containRuntime(raw)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", onWindowError, true);
    window.addEventListener("unhandledrejection", onRejection);
    const sheetSources = loadDocs.map((doc) => ({ path: doc.path, name: doc.name }));
    const treeSources = tree.map((path) => ({ path }));
    const sheetUnsub = embed
      ? subscribeKiCanvasSheetChange(
          embed,
          (page) => {
            const path = pathFromKiCanvasPage(page, treeSources);
            if (path) onFilesUrlChange?.({ file: path });
          },
          { urlFilePath: selectedPath, sources: sheetSources, revisionTree: tree },
        )
      : () => {};
    if (embed) {
      embed.custom_resolver = resolver;
      host.attach(embed);
      embed.addEventListener("error", onEmbedError);
      embed.addEventListener("kicanvas:error", onEmbedError);
      void waitForKiCanvasEmbed(embed)
        .then(() => {
          mountedRef.current = true;
          setEmbedLoaded(true);
          announceFilesViewerMounted(selectedPath);
        })
        .catch((err) => {
          const raw = err instanceof Error ? err.message : String(err);
          if (/did not load/i.test(raw)) return;
          if (isKiCanvasDisposedError(raw) || mountedRef.current || selectedKind !== "schematic") return;
          if (sources[0] === selected) setRuntimeError(kicanvasEmptyStateMessage(raw, selectedKind));
        });
    }
    return () => {
      sheetUnsub();
      window.removeEventListener("error", onWindowError, true);
      window.removeEventListener("unhandledrejection", onRejection);
      embed?.removeEventListener("error", onEmbedError);
      embed?.removeEventListener("kicanvas:error", onEmbedError);
      stop();
      host.dispose();
    };
  }, [webgl, inspectDone, sourceKey, resolver, gitOid, selectedPath, selectedError, selected, selectedKind, docs, org, proj, revId]);

  useLayoutEffect(() => {
    const embed = embedRef.current;
    const root = embed?.shadowRoot;
    if (!root) return;
    let style = root.getElementById("pcbx-icon-font") as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = "pcbx-icon-font";
      root.appendChild(style);
    }
    style.textContent = iconFontReady
      ? ""
      : `.material-symbols-outlined{font-size:0!important;line-height:0;overflow:hidden;color:transparent} kc-ui-activity-side,.activities{visibility:hidden}`;
  }, [iconFontReady, sourceKey, inspectDone]);

  useEffect(() => {
    if (!webgl || selectedError || runtimeError || (inspectDone && sources.length === 0)) {
      announceFilesViewerMounted(selectedPath);
    }
  }, [webgl, selectedError, runtimeError, inspectDone, sources.length, selectedPath]);

  if (!webgl) {
    return (
      <div
        className="flex h-[420px] flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 px-6 text-center"
        data-testid="kicanvas-webgl-missing"
        role="alert"
      >
        <p className="mb-1 text-sm font-medium text-foreground">WebGL2 is required for KiCanvas</p>
        <p className="max-w-md text-sm text-muted-foreground">
          This browser has no WebGL2 context. There is no fallback schematic or PCB viewer.
        </p>
      </div>
    );
  }

  if (deferInspect) {
    const size =
      catalogByteSize(catalogByteSizes, selected) ??
      deferredInspectPaths.reduce(
        (max, path) => Math.max(max, catalogByteSize(catalogByteSizes, path) ?? 0),
        0,
      );
    return (
      <div
        className="flex h-[340px] flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-muted/30 px-6 text-center"
        data-testid="kicanvas-load-large-file"
        data-kicad-path={selectedPath}
        role="status"
      >
        <p className="mb-1 text-sm font-medium text-foreground">Large KiCad file</p>
        <p className="max-w-md text-sm text-muted-foreground">
          This file is about {formatMegabytes(size)} and is not loaded automatically in the viewer.
        </p>
        <Button
          type="button"
          size="sm"
          data-testid="kicanvas-load-large-file-button"
          disabled={loadPending}
          onClick={onConfirmLargeLoad}
        >
          {loadPending ? "Loading…" : "Load in viewer"}
        </Button>
      </div>
    );
  }

  if (selectedError) {
    return (
      <div
        className="flex h-[340px] flex-col items-center justify-center rounded-md border border-dashed border-destructive/40 bg-muted/30 px-6 text-center"
        data-testid="kicanvas-document-error"
        data-kicad-path={selectedPath}
        role="alert"
      >
        <p className="mb-1 text-sm font-medium text-foreground">Unable to open this file</p>
        <p className="max-w-md text-sm text-muted-foreground" data-testid="kicanvas-document-error-reason">
          {selectedError}
        </p>
      </div>
    );
  }

  if (runtimeError) {
    return (
      <div
        className="flex h-[340px] flex-col items-center justify-center rounded-md border border-dashed border-destructive/40 bg-muted/30 px-6 text-center"
        data-testid="kicanvas-document-error"
        data-kicad-path={selectedPath}
        role="alert"
      >
        <p className="mb-1 text-sm font-medium text-foreground">Unable to open this file</p>
        <p className="max-w-md text-sm text-muted-foreground">{runtimeError}</p>
      </div>
    );
  }

  if (sources.length === 0) {
    const empty = inspectDone && sources.length === 0;
    return (
      <div
        className="flex h-[340px] flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 px-6 text-center"
        data-testid="kicanvas-file-view"
        data-kicad-path={selectedPath}
        role="status"
      >
        <div
          data-testid={empty ? "kicanvas-empty-state" : "kicanvas-opening"}
          data-kicad-path={selectedPath}
        >
          <p className="mb-1 text-sm font-medium text-foreground">
            {empty ? "Nothing to show in this file" : "Opening…"}
          </p>
          <p className="max-w-md text-sm text-muted-foreground">
            {empty
              ? selectedKind === "schematic"
                ? "This schematic has no drawable content."
                : "This file has no viewer sources."
              : "Loading this revision."}
          </p>
        </div>
      </div>
    );
  }

  const notices = [
    ...new Set([
      ...issues.map((issue) => issue.message),
      ...missingNotice.map((name) => `Missing subsheet ${name} is not in this revision.`),
      ...(runtimeError ? [runtimeError] : []),
    ]),
  ];

  return (
    <div
      className="relative flex h-[520px] min-h-[420px] w-full flex-col"
      data-testid="kicanvas-file-view"
      data-kicad-path={selectedPath}
      data-kicanvas-icons={iconFontReady ? "ready" : "pending"}
      data-kicanvas-loaded={embedLoaded ? "true" : "false"}
    >
      {notices.length > 0 ? (
        <div
          className="shrink-0 border-b border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground"
          data-testid="kicanvas-missing-notice"
          role="status"
        >
          {notices.join(" ")} The root sheet that exists is still shown.
        </div>
      ) : null}
      {!embedLoaded ? (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-muted/30 px-6 text-center"
          data-testid="kicanvas-opening"
          data-kicad-path={selectedPath}
          role="status"
        >
          <p className="mb-1 text-sm font-medium text-foreground">Opening…</p>
          <p className="max-w-md text-sm text-muted-foreground">Loading this revision.</p>
        </div>
      ) : null}
      <kicanvas-embed
        key={revId}
        ref={(el) => {
          const node = el as KiCanvasEmbedEl | null;
          embedRef.current = node;
          if (node) node.custom_resolver = resolver;
        }}
        controls="full"
        data-testid="kicanvas-embed"
        className="min-h-0 flex-1"
      >
        {docs.map((doc) => (
          <kicanvas-source
            key={`${doc.path}:${doc.name}`}
            name={doc.name}
            data-kicad-path={doc.path}
          >
            {doc.text}
          </kicanvas-source>
        ))}
      </kicanvas-embed>
    </div>
  );
}
